/**
 * Spec 028: the guest's monthly trip picker. A month is 30 days (the backend's monthly tiers
 * are 30, 60 and 90 days, and `classifyRentalType` needs 30 days or more), so N months is
 * exactly 30 × N days and lands in tier N. Day keys are `yyyy-MM-dd`; the zone is the caller's.
 * Tested in `monthlyTrip.test.ts`; the Flutter copy must give the same answers.
 */
import { DateTime } from 'luxon';

export const MONTH_DAYS = 30;
export const MIN_MONTHS = 1;
export const MAX_MONTHS = 12;

export type TripMode = 'dates' | 'months';

export type RateTier = '1-month rate' | '2-month rate' | '3-month rate' | 'under 30 days';

const day = (key: string) => DateTime.fromISO(key, { zone: 'utc' });

export function addDays(key: string, days: number): string {
  return day(key).plus({ days }).toISODate() ?? key;
}

export function daysBetween(startKey: string, endKey: string): number {
  return Math.round(day(endKey).diff(day(startKey), 'days').days);
}

/** The return day of an N-month trip: 30 × N days after the pickup day. */
export function monthlyEnd(startKey: string, months: number): string {
  return addDays(startKey, MONTH_DAYS * months);
}

/** The backend's monthly tier for a day count (`pickMonthlyTier`: 30, 60, 90 days). */
export function tierOf(days: number): RateTier {
  if (days >= 90) return '3-month rate';
  if (days >= 60) return '2-month rate';
  if (days >= 30) return '1-month rate';
  return 'under 30 days';
}

/** Months a free range rounds to when the guest switches back to Months (at least 1). */
export function roundToMonths(days: number): number {
  return Math.min(MAX_MONTHS, Math.max(MIN_MONTHS, Math.round(days / MONTH_DAYS)));
}

/** The most months a trip starting on `startKey` can have without ending after `maxKey`. */
export function maxMonthsFrom(startKey: string, maxKey: string): number {
  let months = 0;
  while (months < MAX_MONTHS && monthlyEnd(startKey, months + 1) <= maxKey) months += 1;
  return months;
}

/** The first blocked day inside `[startKey, endKey]`, or null. */
export function firstBlockedDay(
  startKey: string,
  endKey: string,
  blocked: ReadonlySet<string>,
): string | null {
  if (blocked.size === 0) return null;
  for (let key = startKey; key <= endKey; key = addDays(key, 1)) {
    if (blocked.has(key)) return key;
  }
  return null;
}

/**
 * The next pickup day on or after `fromKey` whose N-month trip crosses no blocked day and ends
 * by `maxKey`; null when there is none.
 */
export function nextFreeStart(
  fromKey: string,
  months: number,
  blocked: ReadonlySet<string>,
  maxKey: string,
): string | null {
  for (let key = fromKey; key <= maxKey; key = addDays(key, 1)) {
    const end = monthlyEnd(key, months);
    if (end > maxKey) return null;
    if (!firstBlockedDay(key, end, blocked)) return key;
  }
  return null;
}

export type MonthlyProblem =
  | { kind: 'short'; days: number }
  | { kind: 'blocked'; day: string; nextStart: string | null };

/** Why a picked range cannot be saved as a monthly trip, or null. */
export function monthlyProblem(
  startKey: string,
  endKey: string,
  months: number,
  blocked: ReadonlySet<string>,
  maxKey: string,
): MonthlyProblem | null {
  const days = daysBetween(startKey, endKey);
  if (days < MONTH_DAYS) return { kind: 'short', days };
  const crossed = firstBlockedDay(startKey, endKey, blocked);
  if (crossed) {
    return {
      kind: 'blocked',
      day: crossed,
      nextStart: nextFreeStart(startKey, months, blocked, maxKey),
    };
  }
  return null;
}

export function monthsLabel(months: number): string {
  return `${months} month${months === 1 ? '' : 's'}`;
}

/** The search card field: "3 months · Nov 4 to Dec 4". */
export function monthlyFieldLabel(months: number, startKey: string, endKey: string): string {
  const fmt = (key: string) => day(key).toFormat('MMM d');
  return `${monthsLabel(months)} · ${fmt(startKey)} to ${fmt(endKey)}`;
}

/** The `mode` and `months` search params as the URL carries them. */
export function parseTripModeParams(params: { mode?: unknown; months?: unknown }): {
  mode: TripMode;
  months: number;
} {
  const months = Number(params.months);
  const safeMonths =
    Number.isInteger(months) && months >= MIN_MONTHS && months <= MAX_MONTHS ? months : MIN_MONTHS;
  return { mode: params.mode === 'months' ? 'months' : 'dates', months: safeMonths };
}

/**
 * Spec 028: "≈ $X/mo" under a result card's trip total. Only for a quote the server priced
 * as MONTHLY (the car rents by the month and the trip is 30 days or more): the total per
 * 30-day month, in whole dollars. Null otherwise, so a daily quote shows no "/mo".
 */
export function perMonthEquivalent(quote: {
  totalDays?: number | null;
  totalPrice?: number | null;
  totalPriceWithDelivery?: number | null;
  rentalType?: string | null;
}): number | null {
  const total = quote.totalPriceWithDelivery ?? quote.totalPrice;
  const days = quote.totalDays;
  if (quote.rentalType !== 'MONTHLY' || !total || !days || days < MONTH_DAYS) return null;
  return Math.round((total * MONTH_DAYS) / days);
}

/** Where a home monthly section's "View all" searches (spec 028). */
export type MonthlySearchPlace = {
  latitude: number;
  longitude: number;
  /** Shown in the search field: "Current location" or "Phoenix, AZ". */
  address: string;
  timezone: string;
};

/** Pickup is 3 days out at 10:00 AM, like the Flutter home and the car page defaults. */
export const MONTHLY_VIEW_ALL_LEAD_DAYS = 3;
export const MONTHLY_VIEW_ALL_TIME = '10:00 AM';

/**
 * Spec 028: the results URL a home monthly section's "View all" opens. One month (30 days)
 * from `today` + 3 days, return time = pickup time, `mode=months&months=1`, so the results
 * page and its search card open in Months mode. Same as Flutter `_openMonthlySearch`, which
 * opens the search form instead. Dates are browser-local midnights, as the search card sends.
 */
export function monthlyViewAllPath(
  place: MonthlySearchPlace,
  today: Date,
  driverAge: number,
): string {
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + MONTHLY_VIEW_ALL_LEAD_DAYS,
  );
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + MONTH_DAYS);
  const params = new URLSearchParams({
    searchMode: 'address',
    mode: 'months',
    months: String(MIN_MONTHS),
    latitude: String(place.latitude),
    longitude: String(place.longitude),
    address: place.address,
    timezone: place.timezone,
    startTime: MONTHLY_VIEW_ALL_TIME,
    returnTime: MONTHLY_VIEW_ALL_TIME,
    startDate: start.toISOString(),
    returnDate: end.toISOString(),
    driverAge: String(driverAge),
    type: 'CAR',
  });
  // %20 for spaces, as the search card's query-string writes them (a literal + is %2B).
  return `/cars/guest/search?${params.toString().replace(/\+/g, '%20')}`;
}

/** The browser's IANA zone, else Los Angeles (the search card's own fallback). */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

/**
 * Spec 028 "similar dates": how many days the start may move. A monthly trip (30 days or
 * more) up to a week either way, a shorter trip up to 3 days. The server caps it at 7.
 */
export function similarDatesFlexDays(totalDays: number): number {
  return totalDays >= MONTH_DAYS ? 7 : 3;
}

/**
 * A search-card date (browser-local midnight, sent as ISO) moved by whole days. The clock time
 * stays, as the server moves the trip in the listing zone.
 */
export function shiftPickerDate(iso: string, days: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + days,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
  ).toISOString();
}

/** "Nov 3 to Dec 3 · 2 days earlier" under a similar dates card. */
export function similarDatesNote(shiftDays: number, startIso: string, endIso: string): string {
  const fmt = (iso: string) =>
    new Date(shiftPickerDate(iso, shiftDays)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  const n = Math.abs(shiftDays);
  const when = `${n} day${n === 1 ? '' : 's'} ${shiftDays < 0 ? 'earlier' : 'later'}`;
  return `${fmt(startIso)} to ${fmt(endIso)} · ${when}`;
}
